import { useEffect, useMemo, useState } from 'react'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import {
  archiveDepartment,
  deleteDepartment,
  listDepartmentCompanyOptions,
  listDepartments,
} from '../api/departmentApi'

const DEFAULT_PAGE_SIZE = 10

function normalizeStatus(department) {
  if (Number(department?.is_archive) === 1) return 'Inactive'
  if (Number(department?.is_approved) === 0 || department?.approval_status === 'PENDING') {
    return 'Pending Review'
  }
  return 'Active'
}

export function useDepartmentListScreen() {
  const [searchText, setSearchText] = useState('')
  const [companyId, setCompanyId] = useState('')
  const [statusFilter, setStatusFilter] = useState('active')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(DEFAULT_PAGE_SIZE)

  const [items, setItems] = useState([])
  const [companyOptions, setCompanyOptions] = useState([])
  const [meta, setMeta] = useState({
    totalCount: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasMore: false,
  })

  const [isLoading, setIsLoading] = useState(true)
  const [isMutating, setIsMutating] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [reloadTick, setReloadTick] = useState(0)

  const queryParams = useMemo(() => {
    const params = {
      page,
      page_size: pageSize,
      includeTotal: true,
    }

    if (statusFilter === 'inactive') {
      params.archive = true
    } else if (statusFilter === 'pending') {
      params.pending_review = true
      params.archive = false
    } else {
      params.archive = false
    }

    if (searchText.trim()) {
      params.search = searchText.trim()
    }

    if (companyId) {
      params.company_id = Number(companyId)
      params.companyId = Number(companyId)
    }

    return params
  }, [companyId, page, pageSize, searchText, statusFilter])

  useEffect(() => {
    let active = true

    async function loadCompanyOptions() {
      try {
        const options = await listDepartmentCompanyOptions({ page: 1, page_size: 150 })
        if (!active) return
        setCompanyOptions(options)
      } catch {
        if (!active) return
        setCompanyOptions([])
      }
    }

    loadCompanyOptions()

    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    let active = true

    async function loadDepartments() {
      setIsLoading(true)
      setError('')

      try {
        const response = await listDepartments(queryParams)
        if (!active) return

        const rows = Array.isArray(response?.items) ? response.items : []
        setItems(rows)
        setMeta({
          totalCount: Number(response?.total || 0),
          page: Number(response?.page || page),
          pageSize: Number(response?.pageSize || pageSize),
          hasMore: Boolean(response?.hasMore),
        })
      } catch (requestError) {
        if (!active) return
        setError(extractApiErrorMessage(requestError, 'Failed to load departments.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadDepartments()

    return () => {
      active = false
    }
  }, [page, pageSize, queryParams, reloadTick])

  const rows = useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        code: item.department_code || '-',
        name: item.name || '-',
        companyName: item.company_name || '-',
        status: normalizeStatus(item),
        isArchived: Number(item?.is_archive) === 1,
      })),
    [items],
  )

  const totalPages = useMemo(() => {
    if (!meta.totalCount || !meta.pageSize) return meta.hasMore ? page + 1 : page
    return Math.max(1, Math.ceil(meta.totalCount / meta.pageSize))
  }, [meta.hasMore, meta.pageSize, meta.totalCount, page])

  const rangeStart = useMemo(() => {
    if (!rows.length) return 0
    return (page - 1) * pageSize + 1
  }, [page, pageSize, rows.length])

  const rangeEnd = useMemo(() => {
    if (!rows.length) return 0
    return rangeStart + rows.length - 1
  }, [rangeStart, rows.length])

  function clearFeedback() {
    setError('')
    setSuccess('')
  }

  function setSearchTextAndReset(value) {
    clearFeedback()
    setSearchText(value)
    setPage(1)
  }

  function setCompanyIdAndReset(value) {
    clearFeedback()
    setCompanyId(value)
    setPage(1)
  }

  function setStatusFilterAndReset(value) {
    clearFeedback()
    setStatusFilter(value)
    setPage(1)
  }

  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages) return
    setPage(nextPage)
  }

  function goToPreviousPage() {
    setPage((prev) => Math.max(1, prev - 1))
  }

  function goToNextPage() {
    if (meta.hasMore || page < totalPages) {
      setPage((prev) => prev + 1)
    }
  }

  async function handleRowDeleteAction(row) {
    clearFeedback()
    setIsMutating(true)

    try {
      if (row.isArchived) {
        await deleteDepartment(row.id)
        setSuccess('Department deleted successfully.')
      } else {
        await archiveDepartment(row.id)
        setSuccess('Department archived successfully.')
      }

      setReloadTick((prev) => prev + 1)
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to update department status.'))
    } finally {
      setIsMutating(false)
    }
  }

  return {
    state: {
      searchText,
      companyId,
      statusFilter,
      page,
      totalPages,
      totalCount: meta.totalCount,
      hasMore: meta.hasMore,
      isLoading,
      isMutating,
      error,
      success,
      rows,
      companyOptions,
      rangeStart,
      rangeEnd,
    },
    actions: {
      setSearchText: setSearchTextAndReset,
      setCompanyId: setCompanyIdAndReset,
      setStatusFilter: setStatusFilterAndReset,
      goToPage,
      goToPreviousPage,
      goToNextPage,
      handleRowDeleteAction,
    },
  }
}
